import { useState } from 'react'
import { Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue'
import { useConcepts } from '../hooks/useKnowledge'
import { categoryColor } from './categoryStyle'
import type { Concept } from '../api/knowledgeApi'

type Picked = Pick<Concept, 'ucid' | 'name' | 'category' | 'artworkCount'>

/** Type to find a concept (name or any merged name); pick one. */
export function ConceptPicker({
  value,
  onChange,
  placeholder = 'Find a concept…',
  exclude = [],
}: {
  value: Picked | null
  onChange: (c: Picked | null) => void
  placeholder?: string
  exclude?: string[]
}) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const query = useDebouncedValue(q, 200)
  const { data, isFetching } = useConcepts({ q: query || undefined, limit: 12, sort: 'artworks' })
  const options = (data?.data ?? []).filter((c) => !exclude.includes(c.ucid))

  if (value) {
    return (
      <div className="flex h-9 items-center justify-between gap-2 rounded-md border bg-white px-3 text-sm">
        <span className="flex min-w-0 items-center gap-2">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: categoryColor(value.category) }} />
          <span className="truncate">{value.name}</span>
          <span className="text-xs tabular-nums text-slate-400">{value.artworkCount.toLocaleString()}</span>
        </span>
        <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-slate-700" aria-label="Clear">
          <X className="h-4 w-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
      <Input
        className="pl-8"
        placeholder={placeholder}
        value={q}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
        }}
      />
      {open && query && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-white shadow-lg">
          {options.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-500">{isFetching ? 'Searching…' : 'No concepts match.'}</div>
          )}
          {options.map((c) => (
            <button
              key={c.ucid}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(c)
                setQ('')
                setOpen(false)
              }}
              className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: categoryColor(c.category) }} />
                <span className="truncate">{c.name}</span>
                {c.memberNames.length > 1 && <span className="text-xs text-slate-400">+{c.memberNames.length - 1} names</span>}
              </span>
              <span className="text-xs tabular-nums text-slate-400">{c.artworkCount.toLocaleString()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
